import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ShipmentRouteViewModel } from '@components/shipment-route-list/shipment-route-map/shipment-route-map.component';
import { LocationDto } from '@entities/deviceInfo';
import { ShipmentJudgedReasonType } from '@entities/shipment';
import { ShipmentStatusViewModel } from '../shipment-info-list-table/shipment-info-list-table.component';
import {
  DeviceInfoViewModel,
  ShipmentAlertMapComponent,
} from './shipment-alert-map/shipment-alert-map.component';
import { ShipmentSensorAlertDetailComponent } from './shipment-sensor-alert-detail/shipment-sensor-alert-detail.component';

/** 配送アラート */
export type AlertModel = {
  /** 配送ID */
  shipmentId: string;
  /** 配送アラート */
  status: string;
  /** 発生時刻 */
  eventAt: string;
  /** 解除時間 */
  resolvedAt?: string;
  /** 判定理由 */
  judgedReason?: ShipmentJudgedReasonType;
};

/** アラート・ワーニング合計 */
export type CountSummaryModel = {
  /** 上限アラート数 */
  alertMaxCount: number;
  /** 下限アラート数 */
  alertMinCount: number;
  /** イベントアラート数 */
  alertEventCount: number;
  /** 上限ワーニング数 */
  warningMaxCount: number;
  /** 下限ワーニング数 */
  warningMinCount: number;
};

export type SensorAlertModel = {
  deviceId?: string;
  deviceName?: string;
  latestEventAt?: string;
  alertDetails?: SensorAlertDetailModel[];
};

export type SensorAlertDetailModel = {
  status: string;
  sensor: string;
  unit: string;
  value?: number;
  threshold?: number;
  location: AlertLocationModel;
  eventAt?: string;
  reportedAt?: string;
  eventType?: string;
};

export type AlertLocationModel = {
  longitude: number;
  latitude: number;
  eventAt: string;
  address?: string;
};

export type ShipmentModel = {
  shipmentName: string;
  deviceName: string;
  shipmentStatus: string;
  isBeforeReady: boolean;
  countSummary: CountSummaryModel;
};

export type DialogTitle = 'デバイス位置' | 'アラート/地図';

export type DialogOption = {
  dialogTitle: DialogTitle;
  shipment: ShipmentModel;
  startDatetime: string;
  arrivalDatetime?: string;
  route: ShipmentRouteViewModel;
  statusHistory?: ShipmentStatusViewModel[];
  alert?: AlertModel[];
  sensorAlert?: SensorAlertModel;
  location?: LocationDto;
  deviceInfoHistory?: DeviceInfoViewModel[];
  hideAlertSections?: boolean;
};

@Component({
  selector: 'app-shipment-info-alert-map-dialog',
  standalone: true,
  imports: [
    ShipmentSensorAlertDetailComponent,
    FormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    ReactiveFormsModule,
    MatFormFieldModule,
    CommonModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatRadioModule,
    MatIcon,
    MatTooltipModule,
    MatIconModule,
    MatButtonModule,
    ShipmentAlertMapComponent,
  ],
  templateUrl: './shipment-info-alert-map-dialog.component.html',
  styleUrl: './shipment-info-alert-map-dialog.component.scss',
})
export class ShipmentInfoAlertMapDialogComponent {
  /** ダイアログタイトル */
  public dialogTitle: DialogTitle | undefined;

  /** 配送名 */
  public shipment: ShipmentModel | undefined;

  /** 配送アラート履歴 */
  public statusHistory?: ShipmentStatusViewModel[] = [];

  /** 配送センサーアラート */
  public shipmentSensorAlert: SensorAlertModel | undefined;

  /** 配送デバイス詳細情報履歴 */
  public shipmentDeviceInfoHistory: DeviceInfoViewModel[] | undefined;

  /** ルート */
  public route: ShipmentRouteViewModel | undefined;

  /** 現在地 */
  public location: LocationDto | undefined;

  /** 出発予定 */
  public startDatetime: string = '';

  /** 到着予定 */
  public arrivalDatetime: string | undefined;

  /** 選択されたセンサーアラート詳細 */
  public selectedSensorAlertDetail: SensorAlertDetailModel | undefined;

  /** 選択されたセンサーアラートマーカー */
  public selectedSensorAlertMarker: SensorAlertDetailModel | undefined;

  /** 配送アラート関連の表示制御 */
  public hideAlertSections = false;

  constructor(@Inject(MAT_DIALOG_DATA) options: DialogOption) {
    setTimeout(() => {
      this.dialogTitle = options.dialogTitle;
      this.shipment = options.shipment;
      this.route = options.route;
      this.statusHistory = options.statusHistory;
      this.startDatetime = options.startDatetime;
      this.arrivalDatetime = options.arrivalDatetime;
      this.shipmentSensorAlert = options.sensorAlert;
      this.location = options.location;
      this.shipmentDeviceInfoHistory = options.deviceInfoHistory;
      this.hideAlertSections = options.hideAlertSections ?? false;
    });
  }

  /**
   * 選択されてたアラートが変更された時
   * @param alertDetail アラート
   */
  public onSensorAlertDetailChanged(alertDetail: SensorAlertDetailModel) {
    this.selectedSensorAlertDetail = alertDetail;
  }

  /**
   * 選択されたマーカ－が変更された時
   * @param marker マーカー
   */
  public onMarkerChanged(marker: SensorAlertDetailModel) {
    this.selectedSensorAlertMarker = marker;
  }
}
